import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { useAuth } from 'src/hooks';

import { useUserStore } from 'src/features/AddEditUsers/store/userStore';

import { getUsersList } from 'src/utils/urls';
import { pageLength } from 'src/utils/appConstants';
import { setListRequestData } from '../utils/reqData';

import { UsersListType, FilterOptionsType } from '../types/usersListType';

type TPage = {
  Data: Array<UsersListType>;
  Draw: number;
  FilteredRecords: number;
  IsSuccess: boolean;
  PageNumber: number;
  StatusCode: number;
  TotalRecords: number;
};

type TError = { message: string };

type TVariables = {
  requestData: any;
  token: string;
  tokenType: string;
  pageParam: any;
};

export async function fetchGetUsersList({
  requestData,
  pageParam,
  token,
  tokenType,
}: TVariables): Promise<TPage> {
  requestData.append('start', `${pageParam}`);
  useUserStore.getState().setListPageParam(pageParam);
  const response = await fetch(getUsersList, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchGetUsersList, 500);

// export type TPage = {
//   data: Array<UsersListType>;
//   draw: number;
//   error: Error | null;
//   isAutoRefreshEnabled: boolean;
//   recordsFiltered: number;
//   recordsTotal: number;
// };

function useGetUsersList(
  filterOptions: FilterOptionsType,
): UseInfiniteQueryResult<TPage, unknown> {
  const { token, tokenType } = useAuth();
  const isFilterSelectionBlank =
    (Array.isArray(filterOptions?.status) &&
      filterOptions.status.length === 0) ||
    (Array.isArray(filterOptions?.permission) &&
      filterOptions.permission.length === 0);

  return useInfiniteQuery(
    ['get_users_list', filterOptions],
    ({ pageParam = 0 }) => {
      const requestData = setListRequestData(filterOptions);
      localStorage.setItem('userPageParam', pageParam);

      return debouncedFetch({
        token,
        tokenType,
        requestData,
        pageParam,
      });
    },
    {
      getNextPageParam: (lastPage: TPage, allPages: Array<TPage>) => {
        const loadedCount = allPages.reduce(
          (sum, page) => sum + (page?.Data?.length ?? 0),
          0,
        );
        if (
          lastPage !== null &&
          lastPage?.Data?.length === pageLength &&
          loadedCount < lastPage?.FilteredRecords
        )
          return loadedCount;
      },
      getPreviousPageParam: (firstPage: TPage, allPages: Array<TPage>) => {
        if (firstPage !== null && firstPage?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },

      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      enabled: !isFilterSelectionBlank,
    },
  );
}

export { useGetUsersList };
